class AnchoredPopover extends HTMLElement {
  constructor() {
    super();
    this.triggerElement = null;
    this.popoverElement = null;
    this.hoverTimeout = null;
    this.interactionDelay = 200;
    this.handleResize = this.debounce(() => {
      if (this.popoverElement?.matches(":popover-open")) {
        this.popoverElement.hidePopover();
      }
    }, 100);
  }

  connectedCallback() {
    this.triggerElement = this.querySelector('[ref="trigger"]');
    this.popoverElement = this.querySelector('[ref="popover"]');

    if (!this.triggerElement || !this.popoverElement) return;

    if (this.dataset.hoverTriggered) {
      this.triggerElement.addEventListener("pointerenter", this.onTriggerEnter);
      this.triggerElement.addEventListener("pointerleave", this.onTriggerLeave);
      this.popoverElement.addEventListener("pointerenter", this.onPopoverEnter);
      this.popoverElement.addEventListener("pointerleave", this.onPopoverLeave);
    }

    if (this.dataset.closeOnResize) {
      this.popoverElement.addEventListener("beforetoggle", this.onBeforeToggle);
    }

    if (!CSS.supports("position-anchor: --trigger")) {
      this.popoverElement.addEventListener("toggle", this.updatePosition);
      this.requestIdle(this.updatePosition);
    }
  }

  disconnectedCallback() {
    if (!this.triggerElement || !this.popoverElement) return;

    this.triggerElement.removeEventListener("pointerenter", this.onTriggerEnter);
    this.triggerElement.removeEventListener("pointerleave", this.onTriggerLeave);
    this.popoverElement.removeEventListener("pointerenter", this.onPopoverEnter);
    this.popoverElement.removeEventListener("pointerleave", this.onPopoverLeave);
    this.popoverElement.removeEventListener("beforetoggle", this.onBeforeToggle);
    this.popoverElement.removeEventListener("toggle", this.updatePosition);
    window.removeEventListener("resize", this.handleResize);

    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  }

  onBeforeToggle = (event) => {
    if (!(event instanceof ToggleEvent)) return;

    window[event.newState === "open" ? "addEventListener" : "removeEventListener"](
      "resize",
      this.handleResize
    );
  };

  onTriggerEnter = () => {
    if (!this.triggerElement || !this.popoverElement) return;

    this.triggerElement.dataset.hoverActive = "true";
    if (!this.popoverElement.matches(":popover-open")) {
      this.hoverTimeout = window.setTimeout(() => {
        if (this.triggerElement?.dataset.hoverActive === "true") {
          this.popoverElement?.showPopover();
        }
      }, this.interactionDelay);
    }
  };

  onTriggerLeave = () => {
    if (!this.triggerElement || !this.popoverElement) return;

    delete this.triggerElement.dataset.hoverActive;
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
    }

    if (this.popoverElement.matches(":popover-open")) {
      this.hoverTimeout = window.setTimeout(() => {
        this.popoverElement?.hidePopover();
      }, this.interactionDelay);
    }
  };

  onPopoverEnter = () => {
    if (this.hoverTimeout) {
      clearTimeout(this.hoverTimeout);
      this.hoverTimeout = null;
    }
  };

  onPopoverLeave = () => {
    if (!this.popoverElement) return;

    this.hoverTimeout = window.setTimeout(() => {
      this.popoverElement?.hidePopover();
    }, this.interactionDelay);
  };

  updatePosition = (event) => {
    if (!this.triggerElement || !this.popoverElement) return;
    if (event instanceof ToggleEvent && event.newState === "closed") return;

    const triggerRect = this.triggerElement.getBoundingClientRect();
    const verticalAnchor = this.getVerticalAnchor() || this.triggerElement;
    const verticalRect = verticalAnchor.getBoundingClientRect();
    const popoverSpacing = 13;
    const spaceBelow = window.innerHeight - verticalRect.bottom - popoverSpacing;
    const spaceAbove = verticalRect.top - popoverSpacing;
    const popoverHeight = this.popoverElement.offsetHeight;
    const flipUp = popoverHeight > spaceBelow && spaceAbove > spaceBelow;

    this.popoverElement.style.setProperty("--anchor-top", `${verticalRect.top}`);
    this.popoverElement.style.setProperty(
      "--anchor-right",
      `${window.innerWidth - triggerRect.right}`
    );
    this.popoverElement.style.setProperty(
      "--anchor-bottom",
      `${window.innerHeight - verticalRect.bottom}`
    );
    this.popoverElement.style.setProperty("--anchor-left", `${triggerRect.left}`);
    this.popoverElement.style.setProperty("--anchor-height", `${verticalRect.height}`);
    this.popoverElement.style.setProperty("--anchor-width", `${triggerRect.width}`);
    this.popoverElement.style.setProperty("--anchor-space-below", `${Math.max(spaceBelow, 0)}`);
    this.popoverElement.style.setProperty("--anchor-space-above", `${Math.max(spaceAbove, 0)}`);
    this.popoverElement.toggleAttribute("data-flip-up", flipUp);
  };

  getVerticalAnchor() {
    const anchorName = this.dataset.verticalPositionAnchor?.trim();
    if (!anchorName) return null;

    const selector = `[data-anchor-name="${CSS.escape(anchorName)}"]`;
    const scope = this.closest("[data-anchor-scope], tr, li, section, article, form");
    const scopedAnchor = scope?.querySelector(selector);
    if (scopedAnchor instanceof HTMLElement) return scopedAnchor;

    const root = this.getRootNode();
    if (root instanceof Document || root instanceof ShadowRoot) {
      const rootAnchor = root.querySelector(selector);
      if (rootAnchor instanceof HTMLElement) return rootAnchor;
    }

    return null;
  }

  debounce(callback, wait) {
    let timeoutId;
    return (...args) => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => callback.apply(this, args), wait);
    };
  }

  requestIdle(callback) {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(callback);
      return;
    }

    window.setTimeout(callback, 1);
  }
}

if (!customElements.get("anchored-popover-component")) {
  customElements.define("anchored-popover-component", AnchoredPopover);
}